import type {
	VonageAuthUrlDto,
	VonageDialOptionsModel,
	VonageDialResponseDto,
	VonageExtensionDto,
	VonageUserDialStatusDto,
	VonageUserExtensionResultDto,
} from '@supply-mover-2-0/shared';
import { ApiService } from './ApiService';

// Type aliases for cleaner exports
export type VonageUserDialStatus = VonageUserDialStatusDto;
export type VonageDialResult = VonageDialResponseDto;
export type VonageExtension = VonageExtensionDto;
export type VonageDialOptions = VonageDialOptionsModel;
export type VonageUserExtensionResult = VonageUserExtensionResultDto;

/**
 * Service for Vonage click-to-dial functionality
 */
class VonageQueryServiceClass {
	private _cachedStatus: VonageUserDialStatusDto | null = null;
	private _cacheTimestamp: number = 0;
	private readonly _cacheDuration = 60000; // 1 minute cache

	/**
	 * Get user dial status - whether the current user can use click-to-dial
	 * Results are cached for 1 minute to avoid excessive API calls
	 */
	public async getUserDialStatus(
		forceRefresh = false,
	): Promise<VonageUserDialStatusDto> {
		const now = Date.now();

		if (
			!forceRefresh &&
			this._cachedStatus != null &&
			now - this._cacheTimestamp < this._cacheDuration
		) {
			return this._cachedStatus;
		}

		try {
			const response = await ApiService.apiGet<VonageUserDialStatusDto>(
				'/integrations/vonage/user-dial-status',
			);

			this._cachedStatus = response;
			this._cacheTimestamp = now;

			return response;
		} catch (error) {
			console.error('Failed to get Vonage user dial status:', error);

			// Return a safe default on error
			return {
				connected: false,
				canDial: false,
				userExtension: null,
				userExtensionName: null,
			};
		}
	}

	/**
	 * Initiate a dial via Vonage
	 */
	public async dial(
		phoneNumber: string,
		_options?: VonageDialOptionsModel,
	): Promise<VonageDialResponseDto> {
		try {
			return await ApiService.apiPost<VonageDialResponseDto>(
				'/integrations/vonage/dial',
				{
					phoneNumber,
				},
			);
		} catch (error) {
			console.error('Failed to initiate dial:', error);

			return {
				success: false,
				error: 'Failed to initiate call. Please try again.',
			};
		}
	}

	/**
	 * Clear the cached status
	 */
	public clearCache(): void {
		this._cachedStatus = null;
		this._cacheTimestamp = 0;
	}

	/**
	 * Get available Vonage extensions for mapping
	 */
	public async getExtensions(): Promise<Array<VonageExtensionDto>> {
		try {
			return await ApiService.apiGet<Array<VonageExtensionDto>>(
				'/integrations/vonage/extensions',
			);
		} catch (error) {
			console.error('Failed to get Vonage extensions:', error);

			return [];
		}
	}

	/**
	 * Get OAuth URL for user self-service extension setup
	 */
	public async getUserAuthUrl(): Promise<VonageAuthUrlDto> {
		return ApiService.apiGet<VonageAuthUrlDto>(
			'/integrations/vonage/user-auth/url',
		);
	}

	/**
	 * Handle OAuth callback for user extension setup
	 * Exchanges the OAuth code and automatically discovers/saves the user's extension
	 */
	public async handleUserAuthCallback(
		code: string,
		state: string,
	): Promise<VonageUserExtensionResultDto> {
		try {
			const result = await ApiService.apiPost<VonageUserExtensionResultDto>(
				'/integrations/vonage/user-auth/callback',
				{ code, state },
			);

			// Clear the cached status so it will be refreshed with the new extension
			this.clearCache();

			return result;
		} catch (error) {
			console.error('Failed to handle user auth callback:', error);

			return {
				success: false,
				error: 'Failed to complete extension setup. Please try again.',
			};
		}
	}
}

export const VonageQueryService = new VonageQueryServiceClass();
